import { useState, useEffect, useRef, Fragment } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatTimeAMPM } from "../lib/utils";
import { getOrgTimezone, getTemporalStatus as getTemporalStatusTZ } from "../lib/timezone";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ClickableAvatar } from "./ClickableAvatar";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "./ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";
import {
  ChevronDown,
  Plus,
  Phone,
  Calendar,
  Clock,
  User,
  MessageSquare,
  CheckCircle2,
  X,
  Check,
  Edit2,
  Trash2,
  History,
  AlertCircle,
  Loader2,
  Pencil,
  Search,
} from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  High: "bg-red-500/15 text-red-500 border-red-500/20",
  Medium: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  Low: "bg-blue-500/15 text-blue-500 border-blue-500/20",
};

const STATUS_COLORS = {
  Open: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Responded: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  "Not Interested": "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  "Not Responded": "bg-orange-500/15 text-orange-500 border-orange-500/20",
  Closed: "bg-zinc-700/15 text-zinc-500 border-zinc-700/20",
  "Need Followup": "bg-purple-500/15 text-purple-500 border-purple-500/20",
};

const TEMPORAL_STATUS_COLORS = {
  Upcoming: "bg-cyan-500/15 text-cyan-500 border-cyan-500/20",
  Overdue: "bg-red-500/15 text-red-500 border-red-500/20",
  Closed: "bg-zinc-500/15 text-zinc-500 border-zinc-500/20",
};

function getTemporalStatus(f, timezone) {
  return getTemporalStatusTZ(f, timezone);
}

const STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Closed", label: "Closed" },
  { value: "Need Followup", label: "Need Followup" },
];


// ─── Follow-up Types inline multi-select + editable + searchable ──────────────────────────
function FollowupTypePicker({ types, setTypes, selectedIds, onChange, placeholder = "Search or create...", createPlaceholder = "Create new type..." }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [newText, setNewText] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const resolveChipStyle = (name) => {
    const colors = [
      { bg: "rgba(99, 86, 255, 0.1)", text: "#6356ff", border: "1px solid rgba(99, 86, 255, 0.2)" },
      { bg: "rgba(236, 72, 153, 0.1)", text: "#ec4899", border: "1px solid rgba(236, 72, 153, 0.2)" },
      { bg: "rgba(16, 185, 129, 0.1)", text: "#10b981", border: "1px solid rgba(16, 185, 129, 0.2)" },
      { bg: "rgba(245, 158, 11, 0.1)", text: "#f59e0b", border: "1px solid rgba(245, 158, 11, 0.2)" },
      { bg: "rgba(59, 130, 246, 0.1)", text: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.2)" },
      { bg: "rgba(139, 92, 246, 0.1)", text: "#8b5cf6", border: "1px solid rgba(139, 92, 246, 0.2)" },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const filtered = search.trim()
    ? types.filter(t => t.name.toLowerCase().includes(search.toLowerCase()))
    : types;

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  const handleCreate = async () => {
    const text = newText.trim();
    if (!text) return;
    if (types.find(t => t.name.toLowerCase() === text.toLowerCase())) {
      toast.error("Already exists");
      return;
    }
    try {
      const res = await api.post("/followups/types", { name: text });
      const newType = res.data.type;
      setTypes(prev => [...prev, newType]);
      onChange([...selectedIds, newType.id]);
      setNewText("");
      setSearch("");
    } catch {
      toast.error("Failed to create followup type");
    }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    if (types.find(t => t.name.toLowerCase() === text.toLowerCase() && t.id !== id)) {
      toast.error("Another type with this name already exists");
      return;
    }
    try {
      await api.put(`/followups/types/${id}`, { name: text });
      setTypes(prev => prev.map(t => t.id === id ? { ...t, name: text } : t));
      setEditingId(null);
      setEditText("");
      toast.success("Type updated successfully");
    } catch {
      toast.error("Failed to update followup type");
    }
  };

  const selectedObjs = types.filter(t => selectedIds.includes(t.id));
  const close = () => {
    setOpen(false);
    setSearch("");
    setEditingId(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          onClick={() => inputRef.current?.focus()}
          className={cn(
            "min-h-[42px] w-full px-3 py-1.5 rounded-lg border text-sm flex flex-wrap items-center gap-1.5 cursor-text transition-all bg-background border-input hover:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary",
            open && "ring-2 ring-primary/20 border-primary"
          )}
        >
          {selectedObjs.map(t => {
            const c = resolveChipStyle(t.name);
            return (
              <span
                key={t.id}
                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: c.bg, color: c.text, border: c.border }}
              >
                {t.name}
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    toggle(t.id);
                  }}
                  className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-black/10 transition-colors ml-1"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={selectedIds.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[100px] bg-transparent outline-none text-foreground placeholder:text-muted-foreground text-sm py-0.5"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-popover shadow-xl border-border rounded-xl overflow-hidden"
        align="start"
        style={{ width: triggerWidth > 0 ? triggerWidth : "auto", minWidth: "240px" }}
      >
        <div className="p-3 border-b border-border space-y-2.5 bg-muted/20">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-input focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/25">
            <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search types..."
              className="flex-1 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder={createPlaceholder}
              className="flex-1 h-8 px-3 text-xs rounded-lg border border-input bg-background outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 text-foreground placeholder:text-muted-foreground"
            />
            <Button
              type="button"
              onClick={handleCreate}
              disabled={!newText.trim()}
              className="h-8 px-3 rounded-lg text-xs font-semibold"
              size="sm"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[220px] p-1 space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              Nothing found &mdash; create one above
            </p>
          ) : (
            filtered.map(t => {
              const isSel = selectedIds.includes(t.id);
              const c = resolveChipStyle(t.name);
              return (
                <div
                  key={t.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors group",
                    isSel ? "bg-primary/10" : "hover:bg-muted"
                  )}
                >
                  {editingId === t.id ? (
                    <input
                      autoFocus
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") saveEdit(t.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="flex-1 h-7 px-2 text-xs rounded border border-primary outline-none bg-background text-foreground"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggle(t.id)}
                      className="flex-1 text-left flex items-center gap-2"
                    >
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all"
                        style={{ background: c.bg, color: c.text, border: c.border }}
                      >
                        {t.name}
                      </span>
                    </button>
                  )}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    {editingId === t.id ? (
                      <button
                        type="button"
                        onClick={() => saveEdit(t.id)}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-emerald-500/10"
                      >
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setEditingId(t.id);
                          setEditText(t.name);
                        }}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-muted-foreground/10"
                      >
                        <Pencil className="w-3 h-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {isSel && editingId !== t.id && (
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="px-3.5 py-2.5 border-t border-border flex items-center justify-between bg-muted/30">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selectedIds.length} selected</span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-destructive hover:text-destructive/80 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            className="text-xs text-primary hover:text-primary/80 font-bold transition-colors uppercase tracking-wider"
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ─── Main Tab ─────────────────────────────────────────────────────────────────
export function MemberFollowupsTab({ member }) {
  const { organization, hasPermission } = useAuth();
  const tz = getOrgTimezone(organization);
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [editFollowup, setEditFollowup] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const canManageFollowups = hasPermission("manage_followups");

  const renderFollowupDetails = (f) => {
    return (
      <div className=" text-sm text-foreground bg-secondary/5 ">
        {/* Action Buttons */}
        <div className="flex items-center gap-2 p-2 border-b border-border/40">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(f);
            }}
          >
            <History className="h-3.5 w-3.5" /> Response Log & Notes
          </Button>
          {
            canManageFollowups && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(f);
                  }}
                >
                  <Edit2 className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs gap-1.5 text-destructive hover:bg-destructive/10 transition-colors ml-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(f.id);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              </>
            )
          }
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3">
          {/* Description */}
          <div className="space-y-1.5 p-2 ">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Description</p>
            <p className="text-xs leading-normal text-muted-foreground whitespace-pre-wrap">{f.description || "No description provided."}</p>
          </div>

          {/* Follow-up Types */}
          <div className="space-y-1.5 p-2 lg:border-l lg:border-border  border-t md:border-t-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Follow-up Types</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {f.followupTypeNames?.length > 0 ? (
                f.followupTypeNames.map((name, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] font-mono">
                    {name}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground text-xs">—</span>
              )}
            </div>
          </div>

          {/* Assignment & Priority */}
          <div className="space-y-1.5 p-2 lg:border-l lg:border-border  border-t md:border-t-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned & Priority</p>
            <div className="space-y-1.5 mt-1">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Staff:</span> {f.assignedStaffName || "Not assigned"}
              </p>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <span className="font-semibold text-foreground">Priority:</span>{" "}
                <Badge className={`${PRIORITY_COLORS[f.priority] || PRIORITY_COLORS.Medium} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>
                  {f.priority}
                </Badge>
              </div>
            </div>
          </div>
        </div>


      </div>
    );
  };

  useEffect(() => { fetchFollowups(); }, [member.id]);

  const fetchFollowups = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/followups?memberId=${member.id}`);
      setFollowups(res.data.followups || []);
    } catch (err) {
      console.error("Failed to fetch followups:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (followup) => {
    try {
      const res = await api.get(`/followups/${followup.id}`);
      setSelectedFollowup({ ...res.data.followup, activities: res.data.activities });
      setShowDetailsModal(true);
    } catch {
      toast.error("Failed to load followup details");
    }
  };

  const handleEdit = (followup) => {
    setEditFollowup(followup);
    setShowAddModal(true);
  };

  const handleDelete = async (followupId) => {
    if (!window.confirm("Are you sure you want to delete this followup?")) return;
    try {
      await api.delete(`/followups/${followupId}`);
      toast.success("Followup deleted");
      fetchFollowups();
    } catch {
      toast.error("Failed to delete followup");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="member-followups-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold">Follow-ups</h3>
          <p className="text-sm text-muted-foreground">
            {followups.length} followup{followups.length !== 1 ? "s" : ""} scheduled
          </p>
        </div>
        {
          canManageFollowups && (
            <Button
              onClick={() => { setEditFollowup(null); setShowAddModal(true); }}
              className="font-heading uppercase tracking-wide text-xs"
              data-testid="add-followup-btn"
            >
              <Plus className="h-4 w-4 mr-2" />Add Followup
            </Button>
          )
        }
      </div>

      {/* Followups list — Table (desktop) + Card stack (mobile) */}
      {followups.length > 0 ? (
        <Card className="bg-card border-border overflow-hidden">

          {/* ─ Desktop table ─ */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Types</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Timing</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned To</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {followups.map((f) => (
                  <Fragment key={f.id}>
                    <TableRow
                      className="group cursor-pointer hover:bg-secondary/50 border-b border-border"
                      onClick={() => toggleRow(f.id)}
                    >
                      <TableCell className="w-10">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(f.id); }}>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRows[f.id] ? "rotate-180" : ""}`} />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{f.title}</p>
                        {f.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{f.description}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono text-sm flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />{f.scheduledDate}
                          </span>
                          {f.scheduledTime && (
                            <span className="font-mono text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {f.followupTypeNames?.length > 0
                            ? f.followupTypeNames.map((name, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] font-mono">{name}</Badge>
                            ))
                            : <span className="text-muted-foreground text-xs">—</span>
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${PRIORITY_COLORS[f.priority] || PRIORITY_COLORS.Medium} border text-[10px] font-mono uppercase`}>
                          {f.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f, tz)]} border text-[10px] font-mono uppercase`}>
                          {getTemporalStatus(f, tz)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[f.status] || STATUS_COLORS.Open} border text-[10px] font-mono uppercase`}>
                          {f.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {f.assignedStaffName || <span className="text-muted-foreground">—</span>}
                        </span>
                      </TableCell>
                    </TableRow>
                    {expandedRows[f.id] && (
                      <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                        <TableCell colSpan={8} className="p-0">
                          {renderFollowupDetails(f)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* ─ Mobile card stack ─ */}
          <div className="md:hidden divide-y divide-border">
            {followups.map((f, idx) => (
              <Collapsible
                key={f.id}
                open={expandedRows[f.id]}
                onOpenChange={() => toggleRow(f.id)}
                asChild
              >
                <div className={`${idx !== followups.length - 1 ? "border-b border-border" : ""} hover:bg-secondary/10 transition-colors`}>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between gap-1 cursor-pointer select-none px-2 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm leading-tight truncate">{f.title}</p>
                        {f.description && (
                          <p className="text-[10px] text-muted-foreground truncate max-w-[200px]">{f.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-mono"><Calendar className="h-3 w-3" />{f.scheduledDate}</span>
                            {f.scheduledTime && <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-mono"><Clock className="h-3 w-3" />{formatTimeAMPM(f.scheduledTime)}</span>}
                          </div>
                          <div className="flex gap-1">
                            {
                              f.status !== "Closed" && (
                                <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(f, tz)]} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>
                                  {getTemporalStatus(f, tz)}
                                </Badge>
                              )
                            }
                            <Badge className={`${STATUS_COLORS[f.status] || STATUS_COLORS.Open} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{f.status}</Badge>
                            <Badge className={`${PRIORITY_COLORS[f.priority] || PRIORITY_COLORS.Medium} border text-[9px] px-1 py-0 h-4 font-mono uppercase`}>{f.priority}</Badge>
                          </div>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 inline ${expandedRows[f.id] ? "rotate-180" : ""}`} />
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {renderFollowupDetails(f)}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">No Follow-ups Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Schedule follow-ups to stay connected with this member
            </p>
            <Button onClick={() => { setEditFollowup(null); setShowAddModal(true); }}>
              <Plus className="h-4 w-4 mr-2" />Add Followup
            </Button>
          </CardContent>
        </Card>
      )}

      <AddFollowupModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        member={member}
        editData={editFollowup}
        onSuccess={fetchFollowups}
      />

      <FollowupDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        followup={selectedFollowup}
        onSuccess={() => {
          fetchFollowups();
          if (selectedFollowup) handleViewDetails(selectedFollowup);
        }}
      />
    </div>
  );
}

// ─── Add / Edit Followup Modal ─────────────────────────────────────────────────

function AddFollowupModal({ open, onOpenChange, member, editData, onSuccess }) {
  const { branches, currentBranch } = useAuth();
  const allowedBranches = branches.filter(b =>
    (member.branchIds && Array.isArray(member.branchIds) && member.branchIds.includes(b.id)) ||
    b.id === member.branchId
  );
  const defaultBranchId = allowedBranches.find(b => b.id === currentBranch?.id)?.id || allowedBranches[0]?.id || member.branchId || "";

  const [followupTypes, setFollowupTypes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const EMPTY_FORM = {
    title: "",
    description: "",
    priority: "Medium",
    followupTypeIds: [],
    scheduledDate: format(new Date(), "yyyy-MM-dd"),
    scheduledTime: "",
    assignedStaffId: "",
    branchId: defaultBranchId,
  };

  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      fetchData();
      setFormData(
        editData
          ? {
            title: editData.title || "",
            description: editData.description || "",
            priority: editData.priority || "Medium",
            followupTypeIds: editData.followupTypeIds || [],
            scheduledDate: editData.scheduledDate || format(new Date(), "yyyy-MM-dd"),
            scheduledTime: editData.scheduledTime || "",
            assignedStaffId: editData.assignedStaffId || "",
            branchId: editData.branchId || defaultBranchId,
          }
          : {
            title: "",
            description: "",
            priority: "Medium",
            followupTypeIds: [],
            scheduledDate: format(new Date(), "yyyy-MM-dd"),
            scheduledTime: "",
            assignedStaffId: "",
            branchId: defaultBranchId,
          }
      );
    }
  }, [open, editData]);

  const fetchData = async () => {
    try {
      const res = await api.get("/followups/types");
      setFollowupTypes(res.data.types || []);
    } catch (err) {
      console.error("Failed to fetch types:", err);
    }
  };

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const res = await api.get(`/followups/staff/list${formData.branchId ? `?branchId=${formData.branchId}` : ""}`);
        setStaffList(res.data.staff || []);
      } catch (err) {
        console.error("Failed to fetch staff:", err);
      }
    };
    if (open) {
      fetchStaff();
    }
  }, [formData.branchId, open]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) { toast.error("Title is required"); return; }
    if (!formData.scheduledDate) { toast.error("Schedule date is required"); return; }
    if (!formData.scheduledTime) { toast.error("Schedule time is required"); return; }

    setLoading(true);
    setSubmitted(true);
    try {
      if (editData) {
        await api.put(`/followups/${editData.id}`, formData);
        toast.success("Followup updated");
      } else {
        await api.post("/followups", { ...formData, memberId: member.id, branchId: formData.branchId || member.branchId || "" });
        toast.success("Followup created");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save followup");
      setSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || submitted;

  return (
    <Dialog open={open} onOpenChange={isDisabled ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-2">
        <DialogHeader className="md:p-4">
          <DialogTitle className="font-heading uppercase tracking-wide">
            {editData ? "Edit Followup" : "Add Followup"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2 md:pr-4 -mr-2 md:-mr-4 [scrollbar-width:thin]">
          <div className="space-y-2 p-2">
            {/* Member chip */}
            <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
              <ClickableAvatar
                src={member.photo}
                name={member.name}
                className="h-10 w-10"
                fallbackClassName="bg-primary text-primary-foreground"
              />
              <div>
                <p className="font-medium text-sm">{member.name}</p>
                <p className="text-xs text-muted-foreground">{member.phone}</p>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Title *</Label>
              <Input
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Renewal Reminder"
                disabled={isDisabled}
                data-testid="followup-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Description / Reason</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add details about this followup..."
                rows={3}
                disabled={isDisabled}
                data-testid="followup-description-input"
              />
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={v => setFormData({ ...formData, priority: v })}
                disabled={isDisabled}
              >
                <SelectTrigger data-testid="followup-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />High</span>
                  </SelectItem>
                  <SelectItem value="Medium">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" />Medium</span>
                  </SelectItem>
                  <SelectItem value="Low">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500" />Low</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Follow-up Types — premium customizable picker */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Follow-up Types</Label>
              <FollowupTypePicker
                types={followupTypes}
                setTypes={setFollowupTypes}
                selectedIds={formData.followupTypeIds}
                onChange={ids => setFormData({ ...formData, followupTypeIds: ids })}
              />
            </div>

            {/* Schedule Date & Time */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase">Schedule Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={e => setFormData({ ...formData, scheduledDate: e.target.value })}
                  disabled={isDisabled}
                  data-testid="followup-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase">Schedule Time *</Label>
                <Input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={e => setFormData({ ...formData, scheduledTime: e.target.value })}
                  disabled={isDisabled}
                  data-testid="followup-time-input"
                />
              </div>
            </div>

            {/* Branch Selector */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Branch *</Label>
              <Select
                value={formData.branchId || ""}
                onValueChange={v => setFormData({ ...formData, branchId: v, assignedStaffId: "" })}
                disabled={isDisabled}
              >
                <SelectTrigger data-testid="followup-branch-select">
                  <SelectValue placeholder="Select a branch" />
                </SelectTrigger>
                <SelectContent>
                  {allowedBranches.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assign Staff */}
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase">Assign Staff</Label>
              <Select
                value={formData.assignedStaffId || "none"}
                onValueChange={v => setFormData({ ...formData, assignedStaffId: v === "none" ? "" : v })}
                disabled={isDisabled}
              >
                <SelectTrigger data-testid="followup-staff-select">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Assignment</SelectItem>
                  {staffList.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDisabled}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isDisabled} data-testid="save-followup-btn">
            {loading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              : editData ? "Update" : "Create Followup"
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Followup Details Modal ────────────────────────────────────────────────────

function FollowupDetailsModal({ open, onOpenChange, followup, onSuccess }) {
  const { organization, hasPermission } = useAuth();
  const tz = getOrgTimezone(organization);
  const [newNote, setNewNote] = useState("");
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [nextTime, setNextTime] = useState("");
  const [loading, setLoading] = useState(false);
  const canManageFollowups = hasPermission('manage_followups');
  if (!followup) return null;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      await api.post(`/followups/${followup.id}/notes`, { notes: newNote.trim() });
      toast.success("Note added");
      setNewNote("");
      onSuccess?.();
    } catch {
      toast.error("Failed to add note");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) { toast.error("Please select a status"); return; }
    if (newStatus === "Need Followup" && (!nextDate || !nextTime)) {
      toast.error("Next schedule date and time is required for 'Need Followup' status"); return;
    }
    setLoading(true);
    try {
      await api.post(`/followups/${followup.id}/status`, {
        status: newStatus, notes: statusNotes,
        nextScheduledDate: nextDate, nextScheduledTime: nextTime,
      });
      toast.success("Status updated");
      setShowStatusChange(false);
      setNewStatus(""); setStatusNotes(""); setNextDate(""); setNextTime("");
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide">Followup Details</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2 md:pr-4 -mr-2 md:-mr-4 [scrollbar-width:thin]">
          <div className="space-y-6 py-4">
            {/* Member */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
              <div className="flex items-center gap-3">
                <ClickableAvatar
                  src={followup.memberPhoto}
                  name={followup.memberName}
                  className="h-12 w-12"
                  fallbackClassName="bg-primary text-primary-foreground"
                />
                <div>
                  <p className="font-heading font-semibold">{followup.memberName}</p>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />{followup.memberPhone}
                  </span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.open(`/members/${followup.memberId}`, "_blank")} className="text-xs">
                View Member
              </Button>
            </div>

            {/* Info */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-heading font-semibold">{followup.title}</h3>
                <div className="flex items-center gap-2">
                  <Badge className={`${TEMPORAL_STATUS_COLORS[getTemporalStatus(followup, tz)]} border text-[10px] font-mono uppercase`}>{getTemporalStatus(followup, tz)}</Badge>
                  <Badge className={`${PRIORITY_COLORS[followup.priority]} border text-[10px] font-mono uppercase`}>{followup.priority}</Badge>
                  <Badge className={`${STATUS_COLORS[followup.status]} border text-[10px] font-mono uppercase`}>{followup.status}</Badge>
                </div>
              </div>
              {followup.description && <p className="text-sm text-muted-foreground">{followup.description}</p>}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Scheduled</p>
                  <p className="font-mono">{followup.scheduledDate}{followup.scheduledTime && ` at ${formatTimeAMPM(followup.scheduledTime)}`}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Assigned To</p>
                  <p>{followup.assignedStaffName || "Not assigned"}</p>
                </div>
              </div>
              {followup.followupTypeNames?.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Follow-up Types</p>
                  <div className="flex flex-wrap gap-1">
                    {followup.followupTypeNames.map((name, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add Note */}
            <div className="space-y-3 p-4 border border-border rounded-xl">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Add Response Note</p>
              <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Enter your followup response or notes..." rows={3} data-testid="followup-note-input" />
              <Button size="sm" onClick={handleAddNote} disabled={loading || !newNote.trim()} data-testid="add-note-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-2" />}
                Add Note
              </Button>
            </div>

            {/* Status Change */}
            {canManageFollowups && (
              <div className="space-y-3 p-4 border border-border rounded-xl">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Change Status</p>
                  {!showStatusChange && (
                    <Button variant="outline" size="sm" onClick={() => setShowStatusChange(true)} className="text-xs" data-testid="change-status-btn">
                      Change Status
                    </Button>
                  )}
                </div>
                {showStatusChange && (
                  <div className="space-y-3">
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="status-select"><SelectValue placeholder="Select new status" /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {newStatus === "Need Followup" && (
                      <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg space-y-3">
                        <div className="flex items-center gap-2 text-purple-500">
                          <AlertCircle className="h-4 w-4" />
                          <span className="text-xs font-medium">Reschedule Required</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label className="text-xs">Next Date *</Label><Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} data-testid="next-date-input" /></div>
                          <div><Label className="text-xs">Next Time *</Label><Input type="time" value={nextTime} onChange={e => setNextTime(e.target.value)} data-testid="next-time-input" /></div>
                        </div>
                      </div>
                    )}
                    <Textarea value={statusNotes} onChange={e => setStatusNotes(e.target.value)} placeholder="Add notes about this status change..." rows={2} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleStatusChange} disabled={loading} data-testid="confirm-status-btn">
                        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                        Update Status
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setShowStatusChange(false); setNewStatus(""); setStatusNotes(""); setNextDate(""); setNextTime(""); }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity History */}
            <div className="space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Activity History</p>
              {followup.activities?.length > 0 ? (
                <div className="space-y-3">
                  {followup.activities.map(activity => (
                    <div key={activity.id} className="flex gap-3 p-3 bg-secondary/30 rounded-lg">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        {activity.action === "created" && <Plus className="h-4 w-4 text-primary" />}
                        {activity.action === "updated" && <Edit2 className="h-4 w-4 text-blue-500" />}
                        {activity.action === "status_changed" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                        {activity.action === "note_added" && <MessageSquare className="h-4 w-4 text-amber-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium capitalize">{activity.action.replace("_", " ")}</p>
                          <span className="text-[10px] text-muted-foreground font-mono">
                            {format(new Date(activity.createdAt), "MMM d, yyyy HH:mm")}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">by {activity.userName}</p>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{activity.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No activity recorded</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
